/**
 * "Reporting this for someone else?" — the incident form's optional colleague.
 *
 * Unobtrusive on purpose. The default is the signed-in person and nothing extra
 * is shown: one small link. Pressing it reveals the directory `EmployeePicker`;
 * choosing a colleague collapses it to "For: <name> (Change)". When the
 * assistant already found the colleague (the person said it was for them), the
 * form opens on that line directly.
 *
 * Choosing yourself is the same as choosing nobody, and a directory entry with
 * no email address is refused here rather than at Submit — the server files
 * for an address.
 */

import { Button, Field, Typography } from "@norican/react";
import { useId, useState } from "react";

import { useT } from "@/i18n";
import { type DirectoryEntry } from "@/lib/api";
import { logger } from "@/lib/logger";

import { isSomeoneElse, type OnBehalfPerson, personFromDirectory } from "../lib/on-behalf";
import { EmployeePicker } from "./employee-picker";

export interface OnBehalfFieldProps {
  /** Who it is for; null for the signed-in person (the default). */
  person: OnBehalfPerson | null;
  onChange: (person: OnBehalfPerson | null) => void;
  /** The signed-in person's address, so picking yourself means "for me". */
  signedInEmail: string | null;
  disabled?: boolean;
  /** Told whether a name is typed in the picker that nobody was picked for (7 Oct). */
  onTextPendingChange?: (pending: boolean) => void;
  /** Shown under the picker: the form's own check ("pick the person from the list"). */
  pickError?: string;
}

export function OnBehalfField({
  person,
  onChange,
  signedInEmail,
  disabled = false,
  onTextPendingChange,
  pickError,
}: OnBehalfFieldProps) {
  const { t } = useT("tickets");
  const pickerId = useId();
  const [open, setOpen] = useState(false);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [noEmail, setNoEmail] = useState(false);

  const handlePick = (entry: DirectoryEntry | null) => {
    setNoEmail(false);
    if (entry === null) {
      setPickedId(null);
      return;
    }
    const chosen = personFromDirectory(entry);
    if (chosen === null) {
      setPickedId(entry.id);
      setNoEmail(true);
      return;
    }
    // The picker closes on a pick, before it could report its box settled.
    onTextPendingChange?.(false);
    const forSomeoneElse = isSomeoneElse(chosen, signedInEmail);
    logger.debug("[tickets] incident is for", forSomeoneElse ? "a colleague" : "the reporter");
    setPickedId(entry.id);
    onChange(forSomeoneElse ? chosen : null);
    setOpen(false);
  };

  const forMe = () => {
    onTextPendingChange?.(false);
    setPickedId(null);
    setNoEmail(false);
    onChange(null);
    setOpen(false);
  };

  if (open) {
    return (
      <div className="flex flex-col gap-2">
        <Field
          name={pickerId}
          label={t("onBehalf.label")}
          hint={t("onBehalf.hint")}
          error={noEmail ? t("onBehalf.noEmail") : pickError}
          disabled={disabled}
        >
          <EmployeePicker
            name={pickerId}
            value={pickedId}
            onChange={handlePick}
            onTextPendingChange={onTextPendingChange}
            disabled={disabled}
            aria-label={t("onBehalf.label")}
          />
        </Field>
        <Button
          type="button"
          variant="ghost"
          tone="neutral"
          size="sm"
          className="self-start"
          onClick={forMe}
          disabled={disabled}
        >
          {t("onBehalf.forMe")}
        </Button>
      </div>
    );
  }

  if (person !== null) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="body-sm" className="font-medium">
            {t("onBehalf.for", { name: person.displayName })}
          </Typography>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen(true)}
            disabled={disabled}
          >
            {t("onBehalf.change")}
          </Button>
        </div>
        <Typography variant="caption" tone="muted">
          {t("onBehalf.hint")} {t("onBehalf.notInList")}
        </Typography>
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="self-start"
      onClick={() => setOpen(true)}
      disabled={disabled}
    >
      {t("onBehalf.toggle")}
    </Button>
  );
}
